import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { UserRowRoleType } from '@modules/users/store/types';
import { getUserTypeFromUserRowRole } from '@modules/users/utils';
import { InviteUserInput, Mutation, UserType } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const INVITE_USERS = gql`
  mutation inviteUser(
    $tenantID: ID!
    $userType: UserType!
    $emails: [String!]
    $triggerInviteEmail: Boolean
  ) {
    inviteUser(
      request: {
        tenantID: $tenantID
        userType: $userType
        emails: $emails
        triggerInviteEmail: $triggerInviteEmail
      }
    ) {
      inviteToken
    }
  }
`;

interface InviteUsersOptions {
  onCompleted?: () => void;
}

export const useInviteUsersByEmail = (options?: InviteUsersOptions) => {
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [inviteUserMutation, { data, loading }] = useMutation<
    Mutation,
    InviteUserInput
  >(INVITE_USERS, {
    notifyOnNetworkStatusChange: true,
    onCompleted: options?.onCompleted,
  });

  const inviteUsers = useCallback(
    async (input: { emails: string[]; role: UserRowRoleType }) => {
      const userType =
        getUserTypeFromUserRowRole(input.role) ?? UserType.Editor;

      try {
        await inviteUserMutation({
          variables: {
            tenantID,
            triggerInviteEmail: true,
            emails: input.emails,
            userType,
          },
        });

        const emailInfo =
          input.emails.length === 1
            ? input.emails[0]
            : `${input.emails.length} users`;

        const roleInfo = input.role ? `to join as ${input.role}` : '';

        showSuccessNotification({
          contentComponent: `Invitation has been sent to ${emailInfo} ${roleInfo}`,
          props: {},
        });
      } catch {
        showErrorNotification({
          contentComponent: `Failed to send invitation.`,
          props: {},
        });
      }
    },
    [
      inviteUserMutation,
      showErrorNotification,
      showSuccessNotification,
      tenantID,
    ],
  );

  return {
    inviteUsers,
    isLoading: loading,
    inviteToken: data?.inviteUser?.inviteToken,
  };
};
